import type { ReactNode } from 'react';
import { Button } from '@heroui/react';
import { ArrowRight, ArrowUpRight, Check, LockKeyhole, RefreshCw } from 'lucide-react';
import './LoginView.css';

/** Una ilustración de marca, sin datos ni controles que se confundan con la mesa real. */
function OfficeIllustration() {
  return (
    <div className="login-scene" aria-hidden="true">
      <svg className="login-building" viewBox="0 0 600 330" fill="none">
        <circle cx="452" cy="81" r="61" fill="#AF63FF" />
        <path d="m78 236 222-91 222 91-222 91Z" fill="#240952" />
        <path d="m116 237 184-75 184 75-184 76Z" stroke="#53317D" />
        <path d="m155 237 145-59 145 59-145 60Z" stroke="#53317D" />
        <path d="M300 162v151M116 237h368M208 200l184 75m-184 0 184-75" stroke="#53317D" />
        <path d="m179 120 123 51v124l-123-51Z" fill="#9182F5" />
        <path d="m302 171 125-51v124l-125 51Z" fill="#757CFC" />
        <path d="m179 120 124-51 124 51-125 51Z" fill="#F6F5F4" />
        <path d="m201 120 102-42 102 42-103 42Z" fill="#DDD5FA" />
        <path d="m247 73 61 25v57l-61-25Z" fill="#C7BCF9" />
        <path d="m308 98 61-25v57l-61 25Z" fill="#ABA2F6" />
        <path d="m247 73 61-25 61 25-61 25Z" fill="#F6F5F4" />
        <path d="m267 92 21 9v28l-21-9Zm62 9 21-9v28l-21 9Z" fill="#2C115A" />
        {[0, 1, 2].map((row) => (
          <g key={row}>
            {[0, 1, 2].map((column) => (
              <g key={column}>
                <path
                  d="m197 143 17 7v23l-17-7Z"
                  transform={`translate(${column * 33} ${column * 13.7 + row * 35})`}
                  fill={row === 1 && column === 1 ? '#E6D1FC' : '#3C226E'}
                />
                <path
                  d="m319 178 17-7v23l-17 7Z"
                  transform={`translate(${column * 33} ${-column * 13.7 + row * 35})`}
                  fill={row === 0 && column === 2 ? '#E6D1FC' : '#342060'}
                />
              </g>
            ))}
          </g>
        ))}
        <path d="M167 212v54m-8 0h16" stroke="#C7BCF9" strokeWidth="3" />
        <circle cx="167" cy="202" r="17" fill="#AF63FF" />
        <path d="M450 245v36m-8 0h16" stroke="#C7BCF9" strokeWidth="3" />
        <circle cx="450" cy="235" r="14" fill="#A69BF2" />
        <path d="M479 28h20v20m0-20-29 29" stroke="#F6F5F4" strokeWidth="2.5" />
      </svg>
      <div className="login-scene-note login-scene-note--priorities">
        <span className="login-note-symbol">
          <span />
        </span>
        <span>
          <small>Tu oficina</small>
          <strong>Prioridades claras.</strong>
        </span>
      </div>
      <div className="login-scene-note login-scene-note--agreements">
        <span className="login-note-symbol">
          <Check size={17} strokeWidth={1.8} />
        </span>
        <span>
          <small>Tu equipo</small>
          <strong>Acuerdos que avanzan.</strong>
        </span>
      </div>
    </div>
  );
}

interface LoginViewProps {
  isLoading: boolean;
  isUnavailable: boolean;
  onLogin: () => void;
  onRetry: () => void;
  authNotice?: ReactNode;
}

export function LoginView({ isLoading, isUnavailable, onLogin, onRetry, authNotice }: LoginViewProps) {
  return (
    <main className="login-page">
      <section className="login-hero" aria-labelledby="login-intro-title">
        <div className="login-hero-header">
          <img src="/brand/eleva-logo-white.svg" alt="Eleva" width="112" height="32" />
          <span>Operativa semanal</span>
        </div>

        <div className="login-hero-content">
          <p className="login-eyebrow">
            <span /> Una oficina. Un equipo.
          </p>
          <h1 id="login-intro-title">
            Lo importante,
            <br />
            <span>a la vista.</span>
          </h1>
          <p className="login-hero-description">
            Una visión compartida para decidir, actuar
            <br className="login-desktop-break" /> y avanzar cada semana.
          </p>
          <OfficeIllustration />
        </div>

        <div className="login-hero-footer">
          <span>Prioridades</span>
          <i />
          <span>Acuerdos</span>
          <i />
          <span>Seguimiento</span>
        </div>
      </section>

      <section className="login-access" aria-labelledby="login-access-title">
        <div className="login-access-top">
          <span>El espacio de tu oficina</span>
          <a href="https://www.eleva.io/es" target="_blank" rel="noopener noreferrer">
            Conoce Eleva <ArrowUpRight size={14} aria-hidden="true" />
            <span className="sr-only"> (se abre en una nueva pestaña)</span>
          </a>
        </div>

        <div className="login-access-body">
          <div className="login-welcome">
            <span className="login-access-kicker">Reunión operativa semanal</span>
            <h2 id="login-access-title">
              Hola.
              <br />
              Vamos a por la semana.
            </h2>
            <p>
              Entra en la mesa de tu oficina. Lo que necesita atención y los próximos pasos de tu equipo, en
              un mismo lugar.
            </p>
          </div>

          <div className="login-action-area">
            <Button
              className="login-submit"
              size="lg"
              isDisabled={isLoading || isUnavailable}
              isPending={isLoading}
              aria-describedby="login-auth-description"
              onPress={onLogin}
            >
              <span>{isLoading ? 'Preparando acceso…' : 'Entrar con Eleva'}</span>
              {!isLoading && <ArrowRight size={19} aria-hidden="true" />}
            </Button>
            <p id="login-auth-description" className="login-auth-description">
              <LockKeyhole size={13} aria-hidden="true" /> Usa tu cuenta habitual de Eleva
            </p>
            {isLoading && (
              <span className="sr-only" role="status">
                Preparando el acceso a Eleva.
              </span>
            )}
            {isUnavailable && (
              <div className="login-config-error" role="alert">
                <p>No se pudo preparar el acceso. Vuelve a intentarlo en unos segundos.</p>
                <Button variant="ghost" size="sm" onPress={onRetry}>
                  <RefreshCw size={14} aria-hidden="true" /> Reintentar
                </Button>
              </div>
            )}
            {authNotice}
          </div>

          <div className="login-access-note">
            <span className="login-access-note-line" />
            <p>
              Tu oficina, tus comunidades.
              <br />
              <span>Con los mismos permisos que ya tienes en Eleva.</span>
            </p>
          </div>
        </div>

        <footer className="login-access-footer">
          <span>Hecho por Eleva. Para trabajar mejor.</span>
          <a href="https://www.eleva.io/es/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacidad<span className="sr-only"> (se abre en una nueva pestaña)</span>
          </a>
        </footer>
      </section>
    </main>
  );
}
